import { useEffect } from 'react'
import { useThree } from '@react-three/fiber'
import { HarmonicFieldRuntime } from '@inyron/animation'
import { HERO_SIGNATURE_EVENT,type HeroSignatureEventDetail } from './HeroCombatRuntimeBridge'
import { heroPerformanceAudioPlan } from './heroPerformanceRuntime'
import { useFightStore } from './store'
import { stageObjectDef } from './arenaStage'
import type { StageMaterial } from './stageDestruction'
import { runtimePublicPath } from './runtimePublicPath'

const AUDIO_ROOT=runtimePublicPath('/audio/fight')
const BANK={
 hit:['impactPunch_medium_000.ogg','impactPunch_medium_001.ogg'],
 heavy:['impactPunch_heavy_000.ogg','impactPunch_heavy_001.ogg'],
 metal:['impactMetal_heavy_000.ogg','impactMetal_heavy_001.ogg','scifi_impactMetal_000.ogg'],
 parry:['impactMetal_light_000.ogg','impactMetal_light_001.ogg'],
 special:['scifi_explosionCrunch_000.ogg','scifi_explosionCrunch_001.ogg'],
 energy:['scifi_forceField_000.ogg','scifi_forceField_001.ogg'],
 low:['scifi_lowFrequency_explosion_000.ogg'],
 wood:['impactWood_heavy_000.ogg','impactWood_heavy_001.ogg'],
} as const
type BankKey=keyof typeof BANK
const allUrls=[...new Set(Object.values(BANK).flat())]
const impactHarmonic=new HarmonicFieldRuntime('fight:impact-audio',{quality:'medium',maxOrder:16,sampleRateHz:192})
function emitAlienAudio(payload:Record<string,unknown>){
 const bridge=(window as typeof window&{__ALIEN_PLAYER_TELEMETRY__?:{emit?:(type:string,payload:Record<string,unknown>)=>void}}).__ALIEN_PLAYER_TELEMETRY__
 bridge?.emit?.('audio.sample',payload)
}

export function settleAudioOperation(operation:Promise<unknown>|undefined):void{
 if(operation)void operation.catch(()=>undefined)
}

function pulse(ctx:AudioContext,frequency:number,volume:number,duration:number,pan:number,type:OscillatorType,delay=0){
 const oscillator=ctx.createOscillator(),gain=ctx.createGain(),panner=ctx.createStereoPanner(),start=ctx.currentTime+delay
 oscillator.type=type;oscillator.frequency.setValueAtTime(frequency,start)
 gain.gain.setValueAtTime(Math.max(.0001,volume),start);gain.gain.exponentialRampToValueAtTime(.0001,start+duration)
 panner.pan.setValueAtTime(Math.max(-1,Math.min(1,pan)),start)
 oscillator.connect(gain).connect(panner).connect(ctx.destination);oscillator.start(start);oscillator.stop(start+duration)
}
function stinger(ctx:AudioContext,kind:'fight'|'round-end'|'victory'){
 if(kind==='fight'){pulse(ctx,180,.035,.08,0,'square');pulse(ctx,360,.03,.1,0,'triangle',.065);return}
 if(kind==='round-end'){pulse(ctx,92,.06,.18,0,'sawtooth');pulse(ctx,62,.045,.22,0,'triangle',.08);return}
 pulse(ctx,220,.04,.12,-.08,'triangle');pulse(ctx,330,.045,.14,0,'triangle',.09);pulse(ctx,495,.05,.18,.08,'sine',.19)
}
async function loadBank(ctx:AudioContext,buffers:Map<string,AudioBuffer>){
 for(const name of allUrls){
  try{const response=await fetch(`${AUDIO_ROOT}/${name}`);if(!response.ok)throw new Error(`${response.status}`);const data=await response.arrayBuffer();buffers.set(name,await ctx.decodeAudioData(data))}
  catch(error){console.warn('[RiftFighters] impact audio unavailable:',name,error)}
  await new Promise<void>(resolve=>window.setTimeout(resolve,45))
 }
}
function playBuffer(ctx:AudioContext,buffers:Map<string,AudioBuffer>,key:BankKey,serial:number,pan:number,gainValue:number,rate=1,delay=0){
 const names=BANK[key],name=names[Math.abs(serial)%names.length],buffer=buffers.get(name);if(!buffer)return false
 const source=ctx.createBufferSource(),gain=ctx.createGain(),panner=ctx.createStereoPanner();source.buffer=buffer;source.playbackRate.value=rate;gain.gain.value=gainValue;panner.pan.value=pan
 source.connect(gain).connect(panner).connect(ctx.destination);source.start(ctx.currentTime+delay);return true
}
function playStageMaterialLayer(ctx:AudioContext,buffers:Map<string,AudioBuffer>,material:StageMaterial|undefined,serial:number,pan:number){
 const key=material;switch(key){case'wood':playBuffer(ctx,buffers,'wood',serial+17,pan,.2,.94);break;case'metal':playBuffer(ctx,buffers,'metal',serial+19,pan,.2,.9);break;case'crystal':playBuffer(ctx,buffers,'energy',serial+23,pan,.14,1.16);playBuffer(ctx,buffers,'parry',serial+29,pan,.08,1.22,.008);break;case'stone':case'earth':playBuffer(ctx,buffers,'heavy',serial+31,pan,.15,.74);playBuffer(ctx,buffers,'low',serial+37,pan,.09,.8,.01);break;case'organic':playBuffer(ctx,buffers,'wood',serial+41,pan,.12,.86);playBuffer(ctx,buffers,'hit',serial+43,pan,.08,.92,.008);break;case'water':playBuffer(ctx,buffers,'energy',serial+47,pan,.09,.82);break}
}
function playLivingResponse(ctx:AudioContext,state:ReturnType<typeof useFightStore.getState>){
 const cue=state.impactCue;if(!cue)return
 const life=cue.source==='player'?state.enemyLife:state.playerLife,pan=Math.max(-1,Math.min(1,cue.x/6))
 const organ=life.lastOrganId?life.organs.find(item=>item.id===life.lastOrganId):undefined
 const base=organ?.role==='control'?132:organ?.role==='circulation'?58:organ?.role==='respiration'?74:organ?.role==='locomotion'?96:organ?.role==='sensor'?176:organ?.role==='energy'?220:84
 const distress=Math.min(1,life.pain*.42+life.stress*.22+(1-life.consciousness)*.2+(1-(organ?.function??1))*.3)
 if(distress>.08)pulse(ctx,base,.004+distress*.018,.06+distress*.07,pan,'sine')
 if(life.electricalLoad>.08)pulse(ctx,420+life.electricalLoad*520,.003+life.electricalLoad*.012,.055,pan,'triangle',.012)
 if(life.reflex!=='none')pulse(ctx,110+life.reflexTimer*180,.003+life.reflexTimer*.018,.04,pan,'sine',.006)
 if(life.heartbeat>.62)pulse(ctx,46+life.heartbeat*34,.003+life.heartbeat*.008,.075,pan,'sine',.028)
}
function playImpact(ctx:AudioContext,buffers:Map<string,AudioBuffer>,state:ReturnType<typeof useFightStore.getState>){
 const cue=state.impactCue;if(!cue)return;const pan=Math.max(-1,Math.min(1,cue.x/6)),attack=(cue.source==='player'?state.player:state.enemy).lastAttack,serial=cue.serial
 const energy=attack==='special'?1:(attack==='heavy'||cue.kind==='guard-break') ? .84 : cue.kind==='parry' ? .68 : .56
 const harmonic=impactHarmonic.sample(serial*.173,{energy01:energy,tension01:cue.kind==='parry'?.82:.48,coherence01:.82,detail01:.78,coupling01:.7,phaseOffset01:Math.abs(cue.x*.173+(cue.y??1.1)*.117)%1})
 const harmonicRate=.96+harmonic.audioBrightness*.08+harmonic.intermodulation*.012
 const harmonicFreq=52+harmonic.fundamentalHz*42+harmonic.spectralCentroid01*76
 pulse(ctx,harmonicFreq,.003+Math.abs(harmonic.intermodulation)*.008,.05+energy*.035,pan,'sine',.004)
 if(cue.stageObjectId)playStageMaterialLayer(ctx,buffers,cue.stageMaterial,cue.serial,pan)
 if(attack==='special'){
  const real=playBuffer(ctx,buffers,'special',serial,pan,.34,.94*harmonicRate)&&playBuffer(ctx,buffers,'energy',serial+1,pan,.18,1.04*harmonicRate,.018)
  playBuffer(ctx,buffers,'low',serial,pan,.2,.88,.012);pulse(ctx,54,real?.025:.055,.22,pan,'sine');return
 }
 if(cue.kind==='guard-break'){playBuffer(ctx,buffers,'metal',serial,pan,.3,.9);playBuffer(ctx,buffers,'heavy',serial+1,pan,.22,.86,.018);pulse(ctx,72,.045,.16,pan,'sawtooth');return}
 if(cue.kind==='parry'){playBuffer(ctx,buffers,'parry',serial,pan,.28,1.08);pulse(ctx,920,.018,.055,pan,'sine');return}
 if(cue.kind==='throw'){playBuffer(ctx,buffers,'heavy',serial,pan,.32,.82);playBuffer(ctx,buffers,'low',serial,pan,.14,.9,.015);pulse(ctx,58,.028,.19,pan,'triangle');return}
 if(cue.kind==='hit'){const key:BankKey=attack==='heavy'?'heavy':'hit';const real=playBuffer(ctx,buffers,key,serial,pan,attack==='heavy'?.32:.24,(attack==='heavy'?.9:1.02)*harmonicRate);pulse(ctx,112+harmonic.bands.high*18,real?.02:.06,.085,pan,'triangle');return}
 playBuffer(ctx,buffers,'metal',serial,pan,.1,1.15);pulse(ctx,165,.018,.06,pan,'sine')
}
export function ImpactAudio(){
 const {scene}=useThree()
 useEffect(()=>{
  scene.userData.inyronImpactAudio={connected:true,unlocked:false,bankEntries:allUrls.length,at:performance.now()}
  let ctx:AudioContext|null=null,loading:Promise<void>|null=null
  const buffers=new Map<string,AudioBuffer>()
  const unlock=()=>{
   ctx??=new AudioContext()
   const active=ctx
   const ready=active.state==='suspended'?active.resume():Promise.resolve()
   settleAudioOperation(ready.then(()=>{if(!loading)loading=loadBank(active,buffers)}))
  }
  window.addEventListener('pointerdown',unlock,{passive:true});window.addEventListener('keydown',unlock,{passive:true})
  const unsubscribe=useFightStore.subscribe((state,previous)=>{
   const impactChanged=state.impactSerial!==previous.impactSerial&&!!state.impactCue
   const impactObservedAt=impactChanged?performance.now():0
   if(impactChanged&&(!ctx||ctx.state!=='running'))emitAlienAudio({missingCue:'impact-audio-context-not-running'})
   if(!ctx||ctx.state!=='running')return
   if(impactChanged){playImpact(ctx,buffers,state);playLivingResponse(ctx,state);emitAlienAudio({desyncMs:performance.now()-impactObservedAt})}
   if(state.stageTransitions.length!==previous.stageTransitions.length&&state.stageTransitions.length){const transition=state.stageTransitions.at(-1)!;const object=state.stageObjects.find(item=>item.id===transition.objectId),def=stageObjectDef(transition.objectId),pan=Math.max(-1,Math.min(1,(def?.position[0]??0)/7)),serial=state.impactSerial+transition.transitionVersion+53;playStageMaterialLayer(ctx,buffers,object?.material,serial,pan);if(transition.to==='collapsed'){playBuffer(ctx,buffers,'low',serial+3,pan,.18,.72);pulse(ctx,46,.04,.24,pan,'sine')}}
   if(state.status!==previous.status){
    if(state.status==='fight')stinger(ctx,'fight')
    else if(state.status==='round-over')stinger(ctx,'round-end')
    else if(state.status==='match-over')stinger(ctx,'victory')
   }
  })
  const onSignature=(event:Event)=>{
   if(!ctx||ctx.state!=='running')return
   const detail=(event as CustomEvent<HeroSignatureEventDetail>).detail
   if(!detail?.performance)return
   const plan=heroPerformanceAudioPlan(detail.performance,detail.side)
   pulse(ctx,plan.frequency,plan.volume,plan.duration,plan.pan,plan.type)
   if(plan.harmonic!==null)pulse(ctx,plan.harmonic,plan.volume*.48,plan.duration*.72,plan.pan,'sine',.018)
  }
  window.addEventListener(HERO_SIGNATURE_EVENT,onSignature)
  return()=>{
   unsubscribe();window.removeEventListener(HERO_SIGNATURE_EVENT,onSignature);window.removeEventListener('pointerdown',unlock);window.removeEventListener('keydown',unlock)
   if(ctx){try{settleAudioOperation(ctx.close())}catch{/* already closed or device lost */}}
   if(scene.userData.inyronImpactAudio)delete scene.userData.inyronImpactAudio
  }
 },[scene])
 return null
}
